import { useEffect } from 'react'
import { useMatch } from 'react-router-dom'
import { chatController } from '@/stores/chatController'
import { useUiStore } from '@/stores/uiStore'
import { MOBILE_QUERY } from './useMediaQuery'
import { useNewChat } from './useNewChat'

/** A Radix dialog/menu/popover handles its own Escape; don't double-handle it. */
function hasOpenLayer(): boolean {
  return Boolean(document.querySelector('[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"]'))
}

/**
 * Ctrl/Cmd+K → focus chat search · Ctrl/Cmd+Shift+O → new chat ·
 * Esc → stop generation, else close the sources panel.
 */
export function useGlobalShortcuts(): void {
  const newChat = useNewChat()
  const match = useMatch('/c/:id')
  const conversationId = match?.params.id ?? null

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const mod = event.ctrlKey || event.metaKey
      const key = event.key.toLowerCase()

      if (mod && !event.shiftKey && !event.altKey && key === 'k') {
        event.preventDefault()
        const ui = useUiStore.getState()
        if (window.matchMedia(MOBILE_QUERY).matches) ui.setMobileSidebarOpen(true)
        else if (ui.sidebarCollapsed) ui.setSidebarCollapsed(false)
        ui.focusSearch()
        return
      }

      if (mod && event.shiftKey && !event.altKey && key === 'o') {
        event.preventDefault()
        newChat()
        return
      }

      if (event.key === 'Escape' && !event.defaultPrevented && !hasOpenLayer()) {
        if (chatController.stop(conversationId)) {
          event.preventDefault()
          return
        }
        const ui = useUiStore.getState()
        if (ui.sourcesPanel.open) {
          event.preventDefault()
          ui.closeSources()
        }
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [newChat, conversationId])
}
